import { useState } from 'react'
import { Plus, UserRound } from 'lucide-react'
import { ApiError } from '@/api/errors'
import { useFaculty, useFacultyMutations, useQuotes, useSegmentMutations } from '@/api/queries'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Overlay'
import { useToast } from '@/components/ui/Toast'
import { ImagePicker } from '@/features/media/ImagePicker'
import type { FacultyMemberRef, FeaturedQuote, Segment, SegmentPatch } from '@/types'

/**
 * Every field of one segment (PC-24): name, headline, description, image
 * (PC-38), featured quote and faculty member. All entered by hand, so nothing
 * here is reviewed.
 *
 * Saves one PATCH with only what changed. The quote list is the client's
 * approved quotes, the faculty list its active members and the image its
 * approved, usable library images, as the API accepts; a current pick that has since been rejected or deactivated is kept
 * until it is changed, and says so.
 */
export function SegmentDetailsModal({
  clientId,
  segment,
  onClose,
}: {
  clientId: string
  segment: Segment | null
  onClose: () => void
}) {
  if (!segment) return null
  // Keyed, so opening another segment starts from its own values.
  return (
    <SegmentDetailsForm key={segment.id} clientId={clientId} segment={segment} onClose={onClose} />
  )
}

function SegmentDetailsForm({
  clientId,
  segment,
  onClose,
}: {
  clientId: string
  segment: Segment
  onClose: () => void
}) {
  const toast = useToast()
  const quotes = useQuotes(clientId).data ?? []
  const faculty = useFaculty(clientId).data ?? []
  const { update } = useSegmentMutations(clientId)

  const [name, setName] = useState(segment.name)
  const [headline, setHeadline] = useState(segment.headline ?? '')
  const [description, setDescription] = useState(segment.description ?? '')
  const [quoteId, setQuoteId] = useState(segment.featuredQuoteId)
  const [facultyId, setFacultyId] = useState(segment.facultyMemberId)
  const [imageId, setImageId] = useState(segment.imageAssetId)
  const [error, setError] = useState<string | null>(null)

  const approved = quotes.filter((q) => q.status === 'approved')
  const quoteOptions: ComboboxOption[] = approved.map((q) => ({
    value: q.id,
    label: q.text.length > 90 ? `${q.text.slice(0, 90)}…` : q.text,
    hint: q.speakerName ?? undefined,
  }))
  const pickedQuote: FeaturedQuote | null =
    quoteId === segment.featuredQuoteId
      ? segment.featuredQuote
      : (() => {
          const q = approved.find((x) => x.id === quoteId)
          return q
            ? {
                id: q.id,
                text: q.text,
                studentName: q.speakerName,
                classYear: q.classYear,
                majors: q.majors,
                studentImageUrl: null,
              }
            : null
        })()
  // The stored pick is no longer approved: kept, but not shown anywhere.
  const quoteHidden =
    quoteId !== null && quoteId === segment.featuredQuoteId && segment.featuredQuote === null

  const active = faculty.filter((f) => f.active)
  const facultyOptions: ComboboxOption[] = active.map((f) => ({
    value: f.id,
    label: f.name,
    hint: f.email ?? undefined,
  }))
  // The current pick stays choosable even once inactive, so the trigger can name it.
  if (segment.facultyMember && !segment.facultyMember.active) {
    facultyOptions.push({
      value: segment.facultyMember.id,
      label: segment.facultyMember.name,
      hint: 'Inactive',
    })
  }
  const pickedFaculty: FacultyMemberRef | null =
    faculty.find((f) => f.id === facultyId) ??
    (facultyId === segment.facultyMemberId ? segment.facultyMember : null)

  const patch: SegmentPatch = {}
  if (name.trim() !== segment.name) patch.name = name.trim()
  if ((headline.trim() || null) !== segment.headline) patch.headline = headline.trim() || null
  if ((description.trim() || null) !== segment.description)
    patch.description = description.trim() || null
  if (quoteId !== segment.featuredQuoteId) patch.featuredQuoteId = quoteId
  if (facultyId !== segment.facultyMemberId) patch.facultyMemberId = facultyId
  if (imageId !== segment.imageAssetId) patch.imageAssetId = imageId
  const dirty = Object.keys(patch).length > 0

  const save = () => {
    setError(null)
    update.mutate(
      { id: segment.id, patch },
      {
        onSuccess: () => {
          toast.push(`Saved ${name.trim()}.`)
          onClose()
        },
        onError: (e) =>
          setError(
            e instanceof ApiError && e.status === 409
              ? 'Another segment already has this name.'
              : e instanceof ApiError && e.status === 422
                ? e.message
                : 'Could not save the segment.',
          ),
      },
    )
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={segment.name}
      description="Segment details. Entered by hand; nothing here needs approval."
      width="max-w-[640px]"
      footer={
        <>
          {error && (
            <p role="alert" className="mr-auto text-[12px] text-brand-red-700">
              {error}
            </p>
          )}
          <Button size="sm" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={update.isPending}
            disabled={!dirty || !name.trim()}
            onClick={save}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" required error={name.trim() ? undefined : 'A segment needs a name.'}>
          {(p) => (
            <Input {...p} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>
        <Field label="Headline">
          {(p) => (
            <Input
              {...p}
              value={headline}
              maxLength={200}
              onChange={(e) => setHeadline(e.target.value)}
            />
          )}
        </Field>
        <Field label="Description">
          {(p) => (
            <Textarea
              {...p}
              rows={4}
              value={description}
              maxLength={5000}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>

        <ImagePicker
          clientId={clientId}
          value={imageId}
          current={{ id: segment.imageAssetId, url: segment.imageUrl }}
          onChange={setImageId}
        />

        <Field
          label="Featured student quote"
          hint={
            approved.length === 0
              ? 'Approve a quote on the Quotes page to feature it here.'
              : 'Approved quotes only.'
          }
          error={
            quoteHidden
              ? 'This quote is no longer approved, so it is not shown. Pick another or clear it.'
              : undefined
          }
        >
          {(p) => (
            <Combobox
              {...p}
              value={quoteId}
              options={quoteOptions}
              onChange={setQuoteId}
              noneLabel="No featured quote"
              searchLabel="Search approved quotes"
            />
          )}
        </Field>
        {pickedQuote && <QuoteCard quote={pickedQuote} />}

        <FacultySection
          clientId={clientId}
          value={facultyId}
          options={facultyOptions}
          picked={pickedFaculty}
          onChange={setFacultyId}
        />
      </div>
    </Modal>
  )
}

function QuoteCard({ quote }: { quote: FeaturedQuote }) {
  const byline = [quote.studentName, quote.classYear, quote.majors].filter(Boolean).join(' · ')
  return (
    <Card className="flex gap-3 p-3">
      <Avatar url={quote.studentImageUrl} />
      <div className="min-w-0">
        <p className="text-[13px] leading-5">“{quote.text}”</p>
        {byline && <p className="mt-1 text-[12px] text-ink-faint">{byline}</p>}
      </div>
    </Card>
  )
}

function FacultySection({
  clientId,
  value,
  options,
  picked,
  onChange,
}: {
  clientId: string
  value: string | null
  options: ComboboxOption[]
  picked: FacultyMemberRef | null
  onChange: (next: string | null) => void
}) {
  const { create } = useFacultyMutations(clientId)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({ name: '', email: '', imageUrl: '' })
  const [error, setError] = useState<string | null>(null)

  const add = () => {
    setError(null)
    create.mutate(
      {
        name: draft.name.trim(),
        email: draft.email.trim() || null,
        imageUrl: draft.imageUrl.trim() || null,
      },
      {
        onSuccess: (member) => {
          onChange(member.id)
          setDraft({ name: '', email: '', imageUrl: '' })
          setAdding(false)
        },
        onError: (e) =>
          setError(
            e instanceof ApiError && e.status === 409
              ? 'A faculty member with this email already exists.'
              : e instanceof ApiError && e.status === 422
                ? 'Check the email and image URL (http or https).'
                : 'Could not add the faculty member.',
          ),
      },
    )
  }

  return (
    <div className="space-y-2">
      <Field
        label="Faculty member"
        error={
          picked && !picked.active
            ? 'This faculty member is inactive. Pick an active one or clear it.'
            : undefined
        }
      >
        {(p) => (
          <Combobox
            {...p}
            value={value}
            options={options}
            onChange={onChange}
            noneLabel="No faculty member"
            searchLabel="Search faculty"
          />
        )}
      </Field>
      {picked && (
        <Card className="flex items-center gap-3 p-3">
          <Avatar url={picked.imageUrl} />
          <div className="min-w-0">
            <p className="text-[13px]">{picked.name}</p>
            {picked.email && <p className="text-[12px] text-ink-faint">{picked.email}</p>}
          </div>
        </Card>
      )}

      {adding ? (
        <Card className="space-y-3 p-3">
          <Field label="Name" required>
            {(p) => (
              <Input
                {...p}
                autoFocus
                value={draft.name}
                maxLength={200}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            )}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Email">
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  value={draft.email}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                />
              )}
            </Field>
            <Field label="Image URL">
              {(p) => (
                <Input
                  {...p}
                  type="url"
                  value={draft.imageUrl}
                  placeholder="https://"
                  onChange={(e) => setDraft({ ...draft, imageUrl: e.target.value })}
                />
              )}
            </Field>
          </div>
          {error && (
            <p role="alert" className="text-[12px] text-brand-red-700">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="quiet"
              onClick={() => setAdding(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={create.isPending}
              disabled={!draft.name.trim()}
              onClick={add}
            >
              Add and select
            </Button>
          </div>
        </Card>
      ) : (
        <Button size="sm" variant="quiet" onClick={() => setAdding(true)}>
          <Plus size={12} aria-hidden />
          New faculty member
        </Button>
      )}
    </div>
  )
}

function Avatar({ url }: { url: string | null }) {
  return url ? (
    <img src={url} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
  ) : (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-sunk text-ink-faint">
      <UserRound size={18} aria-hidden />
    </span>
  )
}
